import React, { useCallback, useMemo } from "react";

import LeagueItemView from "./LeagueItemView";

import {
  RosterManagementLeagueFragment,
  RosterManagementListMyOrganizationsPlayersDocument,
  RosterManagementListMyOrganizationsTeamsDocument,
  useRosterManagementCreateOrganizationLeagueMutation,
  useRosterManagementDeleteOrganizationLeagueMutation,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";

/**
 *
 */
interface Props {
  item: RosterManagementLeagueFragment;
  organizationId: string;
}

const refetchQueries = [
  { query: RosterManagementListMyOrganizationsPlayersDocument },
  { query: RosterManagementListMyOrganizationsTeamsDocument },
];

/**
 * Container for a league list item with add/remove functionality.
 * @param root0 - Component props
 * @param root0.item - League data including id, name, and selection status
 * @param root0.organizationId - Current organization ID for league association
 */
const LeagueItemContainer = ({ item, organizationId }: Props) => {
  const isSmallScreen = useMobileScreen();
  const hasSelectedLeague = useMemo(
    () => item.isMyOrganizationLeague,
    [item.isMyOrganizationLeague]
  );
  const recordCreateOrganizationLeagueGoal = useRecordUserGoal({
    goalType: GoalType.CreateOrganizationLeague,
    value: item.id,
  });
  const recordDeleteOrganizationLeagueGoal = useRecordUserGoal({
    goalType: GoalType.DeleteOrganizationLeague,
    value: item.id,
  });
  const [addLeague] = useRosterManagementCreateOrganizationLeagueMutation({
    onCompleted: data =>
      recordCreateOrganizationLeagueGoal({
        value: data?.createOrganizationLeague?.leagueId,
        goalType: GoalType.CreateOrganizationLeague,
      }),
    variables: {
      input: {
        organizationId,
        leagueId: item.id,
      },
    },
    refetchQueries,
    update: (cache, { data, errors }) => {
      const leagueId = data?.createOrganizationLeague.leagueId;
      if (!!leagueId && !errors) {
        cache.modify({
          id: cache.identify({ __typename: "League", id: leagueId }),
          fields: {
            isMyOrganizationLeague: () => true,
          },
        });
      }
    },
    optimisticResponse: {
      __typename: "Mutation",
      createOrganizationLeague: {
        __typename: "OrganizationLeague",
        leagueId: item.id,
        id: `temp-id-${new Date().toISOString()}`,
        organizationId,
      },
    },
  });

  const [removeLeague] = useRosterManagementDeleteOrganizationLeagueMutation({
    onCompleted: data =>
      recordDeleteOrganizationLeagueGoal({
        value: data?.deleteOrganizationLeague?.leagueId,
        goalType: GoalType.DeleteOrganizationLeague,
      }),
    variables: {
      input: {
        organizationId,
        leagueId: item.id,
      },
    },
    refetchQueries,
    update: (cache, { data, errors }) => {
      const leagueId = data?.deleteOrganizationLeague.leagueId;
      if (!!leagueId && !errors) {
        cache.modify({
          id: cache.identify({ __typename: "League", id: leagueId }),
          fields: {
            isMyOrganizationLeague: () => false,
          },
        });
      }
    },
    optimisticResponse: {
      __typename: "Mutation",
      deleteOrganizationLeague: {
        __typename: "OrganizationLeague",
        leagueId: item.id,
        id: `temp-id-${new Date().toISOString()}`,
        organizationId,
      },
    },
  });

  const handleChange = useCallback(
    (_leagueIds: string[]) => {
      if (!hasSelectedLeague) {
        addLeague();
      } else {
        removeLeague();
      }
    },
    [addLeague, hasSelectedLeague, removeLeague]
  );

  return (
    <LeagueItemView
      item={item}
      isSmallScreen={isSmallScreen}
      hasSelectedLeague={!!hasSelectedLeague}
      onChange={handleChange}
    />
  );
};

export default LeagueItemContainer;
